import React, { useState, useEffect } from 'react';
import { apiClient } from '../../lib/api';
import {
  Announcement,
  AnnouncementListResponse,
  AnnouncementResponse,
  AnnouncementCategory,
  AnnouncementSeverity,
  AnnouncementStatus,
  AnnouncementEmailRecipientMode,
  AnnouncementDeliveryResult,
  CreateAnnouncementRequest,
  UpdateAnnouncementRequest,
  AnnouncementValidation,
  ANNOUNCEMENT_CATEGORY_LABELS,
  ANNOUNCEMENT_SEVERITY_LABELS,
  isValidEmail,
} from '@df-gate-share/shared';
import RecipientChip from '../../components/RecipientChip';
import MessageDialog from '../../components/MessageDialog';
import { useMessageDialog } from '../../hooks/useMessageDialog';

type StatusFilter = 'all' | AnnouncementStatus;
type CategoryFilter = 'all' | AnnouncementCategory;
type SeverityFilter = 'all' | AnnouncementSeverity;

const STATUS_LABELS: Record<AnnouncementStatus, string> = {
  draft: '下書き',
  scheduled: '公開予約',
  published: '公開中',
  expired: '公開終了',
  deleted: '削除済み',
};

const CATEGORY_BADGE_CLASS: Record<AnnouncementCategory, string> = {
  maintenance: 'badge--warning',
  incident: 'badge--danger',
  feature: 'badge--info',
  general: 'badge--revoked',
};

const SEVERITY_BADGE_CLASS: Record<AnnouncementSeverity, string> = {
  high: 'badge--danger',
  normal: 'badge--info',
  low: 'badge--revoked',
};

const STATUS_BADGE_CLASS: Record<AnnouncementStatus, string> = {
  draft: 'badge--revoked',
  scheduled: 'badge--info',
  published: 'badge--active',
  expired: 'badge--expired',
  deleted: 'badge--revoked',
};

/** Form model shared by create/edit modals. Dates are datetime-local strings. */
interface AnnouncementForm {
  title: string;
  body: string;
  category: AnnouncementCategory;
  severity: AnnouncementSeverity;
  publishAt: string; // datetime-local
  publishEndAt: string; // datetime-local (optional)
  emailNotify: boolean;
  emailSendAt: string; // datetime-local (optional)
  sendToRegistered: boolean;
  additionalEmails: string[];
}

const emptyForm = (): AnnouncementForm => ({
  title: '',
  body: '',
  category: 'general',
  severity: 'normal',
  publishAt: '',
  publishEndAt: '',
  emailNotify: false,
  emailSendAt: '',
  sendToRegistered: true,
  additionalEmails: [],
});

/** ISO 8601 -> datetime-local (local time, "YYYY-MM-DDTHH:mm"). */
const isoToLocalInput = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** datetime-local -> ISO 8601, or undefined when empty. */
const localInputToIso = (value: string): string | undefined => {
  if (!value) return undefined;
  const d = new Date(value);
  if (isNaN(d.getTime())) return undefined;
  return d.toISOString();
};

/** Derive the email recipient mode from the toggle + chips. */
const deriveRecipientMode = (
  sendToRegistered: boolean,
  additionalEmails: string[]
): AnnouncementEmailRecipientMode => {
  const hasCustom = additionalEmails.length > 0;
  if (sendToRegistered && hasCustom) return 'both';
  if (!sendToRegistered && hasCustom) return 'custom';
  return 'registered';
};

const formatDate = (iso?: string): string => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const Announcements: React.FC = () => {
  const { dialogState, closeDialog, showAlert } = useMessageDialog();

  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>('all');

  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
  const [cursorHistory, setCursorHistory] = useState<(string | undefined)[]>([]);

  // Create / edit modal state
  const [modalMode, setModalMode] = useState<'create' | 'edit' | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingStatus, setEditingStatus] = useState<AnnouncementStatus | null>(null);
  const [deliveryResult, setDeliveryResult] = useState<AnnouncementDeliveryResult | null>(null);
  const [form, setForm] = useState<AnnouncementForm>(emptyForm());
  const [emailDraft, setEmailDraft] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<Announcement | null>(null);

  useEffect(() => {
    fetchAnnouncements(undefined, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, categoryFilter, severityFilter]);

  const fetchAnnouncements = async (cursor?: string, resetHistory = false) => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (categoryFilter !== 'all') params.append('category', categoryFilter);
      if (severityFilter !== 'all') params.append('severity', severityFilter);
      if (cursor) params.append('cursor', cursor);
      params.append('limit', String(AnnouncementValidation.DEFAULT_PAGE_LIMIT));

      const response = await apiClient.get(
        `/api/v1/admin/announcements?${params.toString()}`
      );
      const data: AnnouncementListResponse = response.data;
      setAnnouncements(data.announcements || []);
      setHasMore(!!data.hasMore);
      setNextCursor(data.nextCursor);
      if (resetHistory) setCursorHistory([]);
    } catch {
      setError('お知らせ一覧の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleNextPage = () => {
    if (hasMore && nextCursor) {
      setCursorHistory((h) => [...h, cursor()]);
      fetchAnnouncements(nextCursor);
    }
  };

  // current page cursor is the last history entry's "next" — track via history stack top
  const cursor = () => (cursorHistory.length > 0 ? cursorHistory[cursorHistory.length - 1] : undefined);

  const handlePrevPage = () => {
    if (cursorHistory.length > 0) {
      const newHistory = [...cursorHistory];
      newHistory.pop();
      setCursorHistory(newHistory);
      const prevCursor = newHistory.length > 0 ? newHistory[newHistory.length - 1] : undefined;
      fetchAnnouncements(prevCursor);
    }
  };

  // ----- Modal open/close -----

  const openCreateModal = () => {
    setForm(emptyForm());
    setEmailDraft('');
    setEmailError('');
    setDeliveryResult(null);
    setEditingId(null);
    setEditingStatus(null);
    setModalMode('create');
  };

  const openEditModal = async (item: Announcement) => {
    setModalMode('edit');
    setEditingId(item.announcementId);
    setEditingStatus(item.status);
    setEmailDraft('');
    setEmailError('');
    setDeliveryResult(null);
    // Seed from list item first, then refresh with detail (delivery result).
    seedFormFrom(item);
    try {
      const response = await apiClient.get(
        `/api/v1/admin/announcements/${item.announcementId}`
      );
      const data: AnnouncementResponse = response.data;
      seedFormFrom(data.announcement);
      setEditingStatus(data.announcement.status);
      setDeliveryResult(data.deliveryResult ?? null);
    } catch {
      // Keep list-seeded values on detail fetch failure.
    }
  };

  const seedFormFrom = (item: Announcement) => {
    const additional = item.additionalEmails ?? [];
    setForm({
      title: item.title,
      body: item.body,
      category: item.category,
      severity: item.severity,
      publishAt: isoToLocalInput(item.publishAt),
      publishEndAt: isoToLocalInput(item.publishEndAt),
      emailNotify: item.emailNotify,
      emailSendAt: isoToLocalInput(item.emailSendAt),
      sendToRegistered:
        item.emailRecipientMode === 'registered' || item.emailRecipientMode === 'both',
      additionalEmails: additional,
    });
  };

  const closeModal = () => {
    setModalMode(null);
    setEditingId(null);
    setEditingStatus(null);
    setDeliveryResult(null);
  };

  const isLocked = editingStatus === 'published' || editingStatus === 'expired';

  // ----- Email chips -----

  const addEmail = () => {
    const value = emailDraft.trim();
    if (!value) return;
    if (!isValidEmail(value)) {
      setEmailError('メールアドレスの形式が正しくありません');
      return;
    }
    if (form.additionalEmails.includes(value)) {
      setEmailError('このメールアドレスは既に追加されています');
      return;
    }
    if (form.additionalEmails.length >= AnnouncementValidation.ADDITIONAL_EMAILS_MAX) {
      setEmailError(`個別指定は最大${AnnouncementValidation.ADDITIONAL_EMAILS_MAX}件です`);
      return;
    }
    setForm((f) => ({ ...f, additionalEmails: [...f.additionalEmails, value] }));
    setEmailDraft('');
    setEmailError('');
  };

  const removeEmail = (email: string) => {
    setForm((f) => ({
      ...f,
      additionalEmails: f.additionalEmails.filter((e) => e !== email),
    }));
  };

  const handleEmailKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addEmail();
    }
  };

  // ----- Submit -----

  const validateForm = (): string | null => {
    const title = form.title.trim();
    if (
      title.length < AnnouncementValidation.TITLE_MIN_LENGTH ||
      title.length > AnnouncementValidation.TITLE_MAX_LENGTH
    ) {
      return `タイトルは${AnnouncementValidation.TITLE_MIN_LENGTH}〜${AnnouncementValidation.TITLE_MAX_LENGTH}文字で入力してください`;
    }
    if (
      form.body.trim().length < AnnouncementValidation.BODY_MIN_LENGTH ||
      form.body.length > AnnouncementValidation.BODY_MAX_LENGTH
    ) {
      return `本文は${AnnouncementValidation.BODY_MIN_LENGTH}〜${AnnouncementValidation.BODY_MAX_LENGTH}文字で入力してください`;
    }
    const publishIso = localInputToIso(form.publishAt);
    const publishEndIso = localInputToIso(form.publishEndAt);
    if (publishIso && publishEndIso && new Date(publishEndIso) <= new Date(publishIso)) {
      return '公開終了日時は公開開始日時より後である必要があります';
    }
    if (form.emailNotify && !form.sendToRegistered && form.additionalEmails.length === 0) {
      return 'メール通知が有効です。送信先を指定してください';
    }
    return null;
  };

  const canSubmit =
    !isSubmitting && form.title.trim().length > 0 && form.body.trim().length > 0;

  const handleCreate = async () => {
    const validationError = validateForm();
    if (validationError) {
      showAlert('warning', '入力エラー', validationError);
      return;
    }
    const publishIso = localInputToIso(form.publishAt) ?? new Date().toISOString();
    const request: CreateAnnouncementRequest = {
      title: form.title.trim(),
      body: form.body,
      category: form.category,
      severity: form.severity,
      publishAt: publishIso,
      publishEndAt: localInputToIso(form.publishEndAt),
      emailNotify: form.emailNotify,
      emailSendAt: form.emailNotify ? localInputToIso(form.emailSendAt) : undefined,
      emailRecipientMode: form.emailNotify
        ? deriveRecipientMode(form.sendToRegistered, form.additionalEmails)
        : undefined,
      additionalEmails:
        form.emailNotify && form.additionalEmails.length > 0
          ? form.additionalEmails
          : undefined,
    };

    try {
      setIsSubmitting(true);
      await apiClient.post('/api/v1/admin/announcements', request);
      closeModal();
      fetchAnnouncements(undefined, true);
      showAlert('success', '作成完了', 'お知らせを作成しました');
    } catch {
      showAlert('error', 'エラー', 'お知らせの作成に失敗しました');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    if (!editingId) return;
    const validationError = validateForm();
    if (validationError) {
      showAlert('warning', '入力エラー', validationError);
      return;
    }

    let request: UpdateAnnouncementRequest;
    if (isLocked) {
      // Only publishEndAt is editable for published/expired.
      request = { publishEndAt: localInputToIso(form.publishEndAt) };
    } else {
      request = {
        title: form.title.trim(),
        body: form.body,
        category: form.category,
        severity: form.severity,
        publishAt: localInputToIso(form.publishAt),
        publishEndAt: localInputToIso(form.publishEndAt),
        emailNotify: form.emailNotify,
        emailSendAt: form.emailNotify ? localInputToIso(form.emailSendAt) : undefined,
        emailRecipientMode: form.emailNotify
          ? deriveRecipientMode(form.sendToRegistered, form.additionalEmails)
          : undefined,
        additionalEmails:
          form.emailNotify && form.additionalEmails.length > 0
            ? form.additionalEmails
            : undefined,
      };
    }

    try {
      setIsSubmitting(true);
      await apiClient.put(`/api/v1/admin/announcements/${editingId}`, request);
      closeModal();
      fetchAnnouncements(cursor(), false);
      showAlert('success', '更新完了', 'お知らせを更新しました');
    } catch {
      showAlert('error', 'エラー', 'お知らせの更新に失敗しました');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget || isSubmitting) return;
    try {
      setIsSubmitting(true);
      await apiClient.delete(`/api/v1/admin/announcements/${deleteTarget.announcementId}`);
      setDeleteTarget(null);
      fetchAnnouncements(undefined, true);
      showAlert('success', '削除完了', 'お知らせを削除しました');
    } catch {
      showAlert('error', 'エラー', 'お知らせの削除に失敗しました');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="card card__body">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <h1 className="page-title">お知らせ設定</h1>
        <button onClick={openCreateModal} className="btn btn--primary">
          + お知らせを作成
        </button>
      </div>

      {/* Filters */}
      <div className="filter-bar mb-6">
        <div>
          <label className="form-label" htmlFor="filter-status">ステータス</label>
          <select
            id="filter-status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            className="form-select"
            style={{ minWidth: '140px' }}
          >
            <option value="all">すべて</option>
            <option value="draft">下書き</option>
            <option value="scheduled">公開予約</option>
            <option value="published">公開中</option>
            <option value="expired">公開終了</option>
          </select>
        </div>
        <div>
          <label className="form-label" htmlFor="filter-category">カテゴリ</label>
          <select
            id="filter-category"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value as CategoryFilter)}
            className="form-select"
            style={{ minWidth: '140px' }}
          >
            <option value="all">すべて</option>
            {(Object.keys(ANNOUNCEMENT_CATEGORY_LABELS) as AnnouncementCategory[]).map((c) => (
              <option key={c} value={c}>{ANNOUNCEMENT_CATEGORY_LABELS[c]}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="form-label" htmlFor="filter-severity">重要度</label>
          <select
            id="filter-severity"
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value as SeverityFilter)}
            className="form-select"
            style={{ minWidth: '120px' }}
          >
            <option value="all">すべて</option>
            {(Object.keys(ANNOUNCEMENT_SEVERITY_LABELS) as AnnouncementSeverity[]).map((s) => (
              <option key={s} value={s}>{ANNOUNCEMENT_SEVERITY_LABELS[s]}</option>
            ))}
          </select>
        </div>
      </div>

      {error && <div className="alert alert--error">{error}</div>}

      {loading ? (
        <div className="text-center text-muted" style={{ padding: 'var(--space-10)' }}>
          読み込み中...
        </div>
      ) : (
        <>
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th>タイトル</th>
                  <th>カテゴリ</th>
                  <th>重要度</th>
                  <th>ステータス</th>
                  <th>公開開始日時</th>
                  <th>メール通知</th>
                  <th>メール送信日時</th>
                  <th>作成日時</th>
                  <th style={{ textAlign: 'center' }}>操作</th>
                </tr>
              </thead>
              <tbody>
                {announcements.map((a) => (
                  <tr key={a.announcementId}>
                    <td style={{ color: 'var(--color-text-primary)' }}>{a.title}</td>
                    <td>
                      <span className={`badge ${CATEGORY_BADGE_CLASS[a.category]}`}>
                        {ANNOUNCEMENT_CATEGORY_LABELS[a.category]}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${SEVERITY_BADGE_CLASS[a.severity]}`}>
                        {ANNOUNCEMENT_SEVERITY_LABELS[a.severity]}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${STATUS_BADGE_CLASS[a.status]}`}>
                        {STATUS_LABELS[a.status]}
                      </span>
                    </td>
                    <td>{formatDate(a.publishAt)}</td>
                    <td>
                      {a.emailNotify ? (
                        <span className="badge badge--info">有</span>
                      ) : (
                        <span className="badge badge--revoked">無</span>
                      )}
                    </td>
                    <td>{a.emailNotify ? formatDate(a.emailSendAt ?? a.publishAt) : '-'}</td>
                    <td>{formatDate(a.createdAt)}</td>
                    <td style={{ textAlign: 'center' }}>
                      <div className="flex flex-col gap-1 items-center" style={{ minWidth: '90px' }}>
                        <button
                          onClick={() => openEditModal(a)}
                          className="btn btn--sm btn--outline-success"
                          style={{ width: '100%' }}
                          title="お知らせを編集"
                        >
                          <span className="material-symbols-outlined icon-sm">edit</span> 編集
                        </button>
                        <button
                          onClick={() => setDeleteTarget(a)}
                          className="btn btn--sm btn--danger"
                          style={{ width: '100%' }}
                          title="お知らせを削除"
                        >
                          <span className="material-symbols-outlined icon-sm">delete</span> 削除
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {announcements.length === 0 && (
              <div className="empty-state">
                <div className="empty-state__desc">お知らせが見つかりません</div>
              </div>
            )}
          </div>

          {announcements.length > 0 && (
            <div className="pagination">
              <div className="pagination__info">{announcements.length}件を表示中</div>
              <div className="pagination__buttons">
                <button
                  onClick={handlePrevPage}
                  disabled={cursorHistory.length === 0}
                  className="btn btn--primary btn--sm"
                >
                  ← 前へ
                </button>
                <button
                  onClick={handleNextPage}
                  disabled={!hasMore}
                  className="btn btn--primary btn--sm"
                >
                  次へ →
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Create / Edit Modal */}
      {modalMode && (
        <div className="modal-overlay">
          <div className="modal modal--wide">
            <h2 className="modal__title">
              {modalMode === 'create' ? 'お知らせの作成' : 'お知らせの編集'}
            </h2>

            {isLocked && (
              <div className="alert alert--info">
                公開中・公開終了のお知らせは公開終了日時のみ変更できます。
              </div>
            )}

            {deliveryResult && (
              <div className="info-box info-box--info mb-6">
                <div className="font-semibold mb-2">メール送信結果</div>
                <div>
                  成功: {deliveryResult.successCount} / 失敗: {deliveryResult.failureCount} / 合計: {deliveryResult.totalCount}
                </div>
              </div>
            )}

            <div className="form-group">
              <label className="form-label form-label--required" htmlFor="ann-title">タイトル</label>
              <input
                id="ann-title"
                type="text"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="例: メンテナンスのお知らせ"
                className="form-input"
                maxLength={AnnouncementValidation.TITLE_MAX_LENGTH}
                disabled={isLocked}
              />
            </div>

            <div className="form-group">
              <label className="form-label form-label--required" htmlFor="ann-body">本文</label>
              <textarea
                id="ann-body"
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                placeholder="本文を入力してください（HTMLタグ使用可、保存時にサニタイズされます）"
                className="form-textarea"
                rows={8}
                maxLength={AnnouncementValidation.BODY_MAX_LENGTH}
                disabled={isLocked}
              />
              <div className="form-hint">改行はそのまま表示に反映されます。HTMLタグも使用できます（許可タグのみ保存されます）。</div>
            </div>

            <div className="flex gap-4">
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label form-label--required" htmlFor="ann-category">カテゴリ</label>
                <select
                  id="ann-category"
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value as AnnouncementCategory })}
                  className="form-select"
                  disabled={isLocked}
                >
                  {(Object.keys(ANNOUNCEMENT_CATEGORY_LABELS) as AnnouncementCategory[]).map((c) => (
                    <option key={c} value={c}>{ANNOUNCEMENT_CATEGORY_LABELS[c]}</option>
                  ))}
                </select>
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label form-label--required" htmlFor="ann-severity">重要度</label>
                <select
                  id="ann-severity"
                  value={form.severity}
                  onChange={(e) => setForm({ ...form, severity: e.target.value as AnnouncementSeverity })}
                  className="form-select"
                  disabled={isLocked}
                >
                  {(Object.keys(ANNOUNCEMENT_SEVERITY_LABELS) as AnnouncementSeverity[]).map((s) => (
                    <option key={s} value={s}>{ANNOUNCEMENT_SEVERITY_LABELS[s]}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label" htmlFor="ann-publishAt">公開開始日時</label>
                <input
                  id="ann-publishAt"
                  type="datetime-local"
                  value={form.publishAt}
                  onChange={(e) => setForm({ ...form, publishAt: e.target.value })}
                  className="form-input"
                  disabled={isLocked}
                />
                <div className="form-hint">
                  未指定の場合は即時公開されます。未来日時を指定した予約公開は、定期処理により反映されるため公開開始日時から最大約1時間のラグが発生します。
                </div>
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label" htmlFor="ann-publishEndAt">公開終了日時</label>
                <input
                  id="ann-publishEndAt"
                  type="datetime-local"
                  value={form.publishEndAt}
                  onChange={(e) => setForm({ ...form, publishEndAt: e.target.value })}
                  className="form-input"
                />
                <div className="form-hint">
                  未指定の場合は公開開始から{AnnouncementValidation.DEFAULT_PUBLISH_END_OFFSET_DAYS}日後。
                </div>
              </div>
            </div>

            {/* Email notify toggle */}
            <div className="form-group">
              <label className="flex items-center gap-2" style={{ cursor: isLocked ? 'not-allowed' : 'pointer' }}>
                <input
                  type="checkbox"
                  aria-label="メール通知"
                  checked={form.emailNotify}
                  onChange={(e) => setForm({ ...form, emailNotify: e.target.checked })}
                  disabled={isLocked}
                />
                <span className="form-label" style={{ margin: 0 }}>メール通知</span>
              </label>
            </div>

            {/* Email settings block */}
            {form.emailNotify && (
              <div className="info-box info-box--info mb-6">
                <div className="form-group">
                  <label className="form-label" htmlFor="ann-emailSendAt">メール送信日時</label>
                  <input
                    id="ann-emailSendAt"
                    type="datetime-local"
                    value={form.emailSendAt}
                    onChange={(e) => setForm({ ...form, emailSendAt: e.target.value })}
                    className="form-input"
                    disabled={isLocked}
                  />
                  <div className="form-hint">
                    未指定の場合は公開開始日時に送信されます。送信は定期処理により実行されるため、指定日時から最大約1時間のラグが発生します。
                  </div>
                </div>

                <div className="form-group">
                  <label className="flex items-center gap-2" style={{ cursor: isLocked ? 'not-allowed' : 'pointer' }}>
                    <input
                      type="checkbox"
                      aria-label="登録ユーザー全員に送信"
                      checked={form.sendToRegistered}
                      onChange={(e) => setForm({ ...form, sendToRegistered: e.target.checked })}
                      disabled={isLocked}
                    />
                    <span className="form-label" style={{ margin: 0 }}>登録ユーザー全員に送信</span>
                  </label>
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="ann-additionalEmails">個別指定アドレス</label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {form.additionalEmails.map((email) => (
                      <RecipientChip key={email} email={email} onRemove={removeEmail} />
                    ))}
                  </div>
                  <input
                    id="ann-additionalEmails"
                    type="email"
                    value={emailDraft}
                    onChange={(e) => { setEmailDraft(e.target.value); setEmailError(''); }}
                    onKeyDown={handleEmailKeyDown}
                    onBlur={addEmail}
                    placeholder="メールアドレスを入力しEnter"
                    className="form-input"
                    disabled={isLocked}
                  />
                  {emailError && <div className="form-hint" style={{ color: 'var(--color-danger)' }}>{emailError}</div>}
                </div>
              </div>
            )}

            <div className="modal__actions">
              <button onClick={closeModal} className="btn btn--secondary">
                キャンセル
              </button>
              <button
                onClick={modalMode === 'create' ? handleCreate : handleUpdate}
                disabled={!canSubmit}
                className="btn btn--primary"
              >
                {isSubmitting
                  ? (modalMode === 'create' ? '作成中...' : '更新中...')
                  : (modalMode === 'create' ? '作成' : '更新')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteTarget && (
        <div className="modal-overlay">
          <div className="modal" role="dialog">
            <h2 className="modal__title">お知らせの削除</h2>
            <p className="text-muted mb-6" style={{ fontSize: 'var(--text-base)' }}>
              お知らせ「{deleteTarget.title}」を削除してもよろしいですか？
              削除したお知らせは一覧・ユーザー画面から除外されます。
            </p>
            <div className="modal__actions">
              <button
                onClick={() => setDeleteTarget(null)}
                className="btn btn--secondary"
              >
                キャンセル
              </button>
              <button
                onClick={handleDelete}
                disabled={isSubmitting}
                className="btn btn--danger"
              >
                {isSubmitting ? '処理中...' : '削除する'}
              </button>
            </div>
          </div>
        </div>
      )}

      <MessageDialog {...dialogState} onClose={closeDialog} />
    </div>
  );
};

export default Announcements;
